import { Link, useLocation } from 'react-router-dom';
import type { TerritoryResolution, TerritoryCategoryLink } from '@buzzmap/shared';
import { territoryCanonicalPath, territoryPeriodHref } from '../../utils/categoryLinks';
import { parsePresetRoute, requiresTerritoryDocumentNavigation } from '../../routes/territoryPaths';
import styles from './TerritoryHeader.module.css';

interface TerritoryHeaderProps {
  resolution: TerritoryResolution | null;
  /**
   * Suppress the plain-territory "Esplora per categoria" list. Set only by
   * the two home screens (HomePage mobile chips + HomeDesktop tiles), which
   * render the same categories as real `<a href>` links further down — with
   * both, the section appeared twice on `/eventi/:territory` and Google had
   * two copies to index. Category-page nav ("Altre categorie", the parent
   * link, "Tutti gli eventi — {name}") is never affected by this prop; the
   * Esplora screens use `hideCategoryNav` instead (see below).
   */
  hideCategoryList?: boolean;
  /**
   * Suppress the ENTIRE category nav — "Altre categorie", "Tutti gli eventi
   * — {name}", and on a plain territory the "Esplora per categoria" list.
   * Set by the two Esplora screens, whose CategoryChipRow is in territory
   * mode: every one of those targets is a chip `<a href>` there, so the
   * header copy would be a second category row on the same page (and a
   * second set for Google to index).
   *
   * `parentCategoryLink` (the link up to the parent territory's same
   * category page) is deliberately NOT suppressed — no chip points there,
   * so it stays as the one small link under the heading.
   */
  hideCategoryNav?: boolean;
  /**
   * `plain` drops the light card and sets the heading as a headline, for the
   * mobile home's blue band (white text). `plain-light` is the same headline
   * on a light background, for the desktop home content column. Same elements
   * and text either way: only the look changes.
   *
   * `esplora` is the Esplora desktop header (approved mockup "Striscia B"):
   * no card, aligned with the page content column, a breadcrumb over the
   * heading, the heading at 24px display size, and `filters` inline on the
   * heading row. Nasconde il conteggio categoria e la navigazione categorie,
   * già nei filtri e nei chip; mantiene il conteggio del periodo del server.
   * Rendered only by `EsploraDesktopLayout`; the home screens and the mobile
   * Esplora keep the other variants unchanged.
   */
  variant?: 'card' | 'plain' | 'plain-light' | 'esplora';
  /**
   * The Esplora desktop filter controls (location pill, date pill, counter,
   * map toggle). Rendered right after the heading on the same row, so the
   * title and the filters wrap together when the row gets short. Only read
   * by the `esplora` variant; ignored elsewhere.
   */
  filters?: React.ReactNode;
}

const VARIANT_CLASS: Record<NonNullable<TerritoryHeaderProps['variant']>, string> = {
  card: `${styles.header}`,
  plain: `${styles.header} ${styles.plain}`,
  'plain-light': `${styles.header} ${styles.plain} ${styles.plainLight}`,
  esplora: `${styles.header} ${styles.esplora}`,
};

/** Same phrasing as `categoryCountLine` in `api/lib/render/html.ts` (T006-BE). */
function countLine(n: number): string {
  if (n === 0) return 'Nessun evento in programma al momento.';
  const word = n === 1 ? 'evento' : 'eventi';
  return `${n} ${word} in programma`;
}

function CategoryLinkList({ links }: { links: TerritoryCategoryLink[] }) {
  return (
    <ul className={styles.linkList}>
      {links.map((l) => (
        <li key={l.slug}>
          <Link to={l.path} className={styles.link}>
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * App-side header for a territory or territory+category route (T014-FE).
 * Reproduces the server shell's `<h1>`, count line and category links block
 * verbatim (R6) so the DOM after `removeSsrShell()` matches what the SSR
 * shell already showed — same text, same `href`s. Exception: on the two home
 * screens the plain-territory category links block is dropped
 * (`hideCategoryList`) because the tiles/chips below render it as crawlable
 * `<a href>` links, and on the two Esplora screens the whole category nav is
 * dropped (`hideCategoryNav`) because the territory-mode chip row below
 * carries those links; the shell copy goes away with the shell.
 *
 * The server shell's PERIOD link row (`periodLinks`, "Oggi · Questo weekend ·
 * Ottobre · …") is deliberately NOT reproduced: it is a second date filter
 * under the heading and the date popup is the single date control (it reaches
 * the same period pages). `periodLinks` is still read — by
 * `useFilters.acceptsPeriod` — as the server's "this territory has period
 * pages" flag, so it is not dead data.
 *
 * Renders nothing when `resolution` is null (plain `/` and `/esplora`).
 */
export function TerritoryHeader({
  resolution,
  hideCategoryList = false,
  hideCategoryNav = false,
  variant = 'card',
  filters,
}: TerritoryHeaderProps) {
  const { pathname, search } = useLocation();
  if (!resolution) return null;

  // Defensive against old-shape /api/territory responses: production hasn't
  // deployed the new fields yet, and the PWA service worker can also serve a
  // stale cached payload after a deploy. `categoryLinks` missing degrades to
  // "no links"; a missing `heading` renders nothing rather than "undefined"
  // unless there's at least a `name` to build a fallback heading from.
  const categoryLinks = resolution.categoryLinks ?? [];
  const { category, parentCategoryLink } = resolution;
  const periodSlug = parsePresetRoute(pathname, search)?.periodSlug ?? resolution.period?.slug;
  const parentHref = parentCategoryLink
    ? territoryPeriodHref(
        `${parentCategoryLink.path}${periodSlug ? `?quando=${periodSlug}` : ''}`,
        search
      )
    : null;
  const heading =
    resolution.heading ?? (resolution.name ? `Eventi a ${resolution.name} e dintorni` : null);
  if (!heading) return null;

  const isEsplora = variant === 'esplora';

  // A category page always has at least the "Tutti gli eventi — {name}" link
  // back to the plain territory page — unless the Esplora chip row took the
  // whole nav over (`hideCategoryNav`), in which case only the parent link
  // (if any) is left to render. A plain territory page's nav is worth
  // rendering only when it has sibling category links to show and neither
  // the home screens (`hideCategoryList`) nor the Esplora chips
  // (`hideCategoryNav`) have taken that section over.
  // On `esplora` the nav never renders: the parent link moved up into the
  // breadcrumb and the chips carry the rest (rendering it twice would also
  // put the same href on the page twice).
  const showNav =
    !isEsplora &&
    (category != null
      ? Boolean(parentCategoryLink) || !hideCategoryNav
      : !hideCategoryNav && !hideCategoryList && categoryLinks.length > 0);

  return (
    <header className={VARIANT_CLASS[variant]}>
      {isEsplora && parentCategoryLink && parentHref && (
        <nav aria-label="Percorso" className={styles.breadcrumb}>
          <Link
            to={parentHref}
            reloadDocument={requiresTerritoryDocumentNavigation(pathname, search, parentHref)}
            className={styles.crumbLink}
          >
            {parentCategoryLink.label}
          </Link>
          <span className={styles.crumbSep} aria-hidden="true">
            &rsaquo;
          </span>
          <span className={styles.crumbCurrent} aria-current="page">
            {resolution.name}
          </span>
        </nav>
      )}

      <div className={styles.titleBlock}>
        <h1 className={styles.heading}>{heading}</h1>
        {isEsplora && filters}
      </div>

      {(resolution.period || (!isEsplora && category)) && (
        <p className={styles.countLine}>
          {resolution.period
            ? `${resolution.upcomingCount ?? 0} ${(resolution.upcomingCount ?? 0) === 1 ? 'evento' : 'eventi'} nel periodo`
            : countLine(resolution.upcomingCount ?? 0)}
        </p>
      )}

      {/* The period chips ("Oggi · Questo weekend · Ottobre · …") that used to
          sit here are gone: the date popup is the single date control, and it
          navigates to the very same period pages (which stay listed in the
          sitemap, so Google still finds them). The heading, the count line and
          the fallback message above/below still report the active period. */}

      {resolution.periodFallbackMessage && (
        <p className={styles.periodFallbackMessage} role="status">
          {resolution.periodFallbackMessage}
        </p>
      )}

      {showNav && (
        <nav aria-label="Categorie" className={styles.nav}>
          {category ? (
            <>
              {parentCategoryLink && parentHref && (
                <p>
                  <Link
                    to={parentHref}
                    reloadDocument={requiresTerritoryDocumentNavigation(
                      pathname,
                      search,
                      parentHref
                    )}
                    className={styles.link}
                  >
                    {parentCategoryLink.label}
                  </Link>
                </p>
              )}
              {!hideCategoryNav && (
                <>
                  {categoryLinks.length > 0 && (
                    <div>
                      <p className={styles.linkListLabel}>Altre categorie</p>
                      <CategoryLinkList links={categoryLinks} />
                    </div>
                  )}
                  <p>
                    <Link to={territoryCanonicalPath(resolution)} className={styles.link}>
                      Tutti gli eventi — {resolution.name}
                    </Link>
                  </p>
                </>
              )}
            </>
          ) : (
            categoryLinks.length > 0 && (
              <div>
                <p className={styles.linkListLabel}>Esplora per categoria</p>
                <CategoryLinkList links={categoryLinks} />
              </div>
            )
          )}
        </nav>
      )}
    </header>
  );
}
